import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { CameraIcon, TrashIcon } from "@heroicons/react/24/outline";
import { ImageWithFallback } from "../../../../Utils/loader";
import { UrduInput } from "../../../../Components/Shared/UrduInput";
import { ImageSeoFields } from "../../../../Components/Shared/ImageSeoFields";
import { UiModal } from "../../../../Components/Shared/UiModal";

const EMPTY_FORM = {
  file: null,
  name: "",
  brandName_ur: "",
  preview: null,
  image_alt: "",
  image_title: "",
};

const NAME_PATTERN = /^[A-Za-z\s]+$/;
const NAME_INPUT_PATTERN = /^[A-Za-z\s]*$/;

function BrandModal({ isOpen, onClose, editingBrand, onSubmit }) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);

  const isEditMode = Boolean(editingBrand);

  const revokeBlobPreview = (preview) => {
    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setFormData((prev) => {
        revokeBlobPreview(prev.preview);
        return EMPTY_FORM;
      });
      setIsSaving(false);
      return;
    }

    if (editingBrand) {
      setFormData({
        file: null,
        name: editingBrand.name || "",
        brandName_ur: editingBrand.brandName_ur || "",
        preview: editingBrand.image || null,
        image_alt: editingBrand.image_alt || "",
        image_title: editingBrand.image_title || "",
      });
    } else {
      setFormData(EMPTY_FORM);
    }
    setIsSaving(false);
  }, [isOpen, editingBrand]);

  const handleClose = () => {
    revokeBlobPreview(formData.preview);
    setFormData(EMPTY_FORM);
    setIsSaving(false);
    onClose();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      Swal.fire("Error!", "Please select a valid image file.", "error");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      Swal.fire("Error!", "Logo size should be less than 2MB.", "error");
      return;
    }

    const img = new Image();
    const preview = URL.createObjectURL(file);

    img.onload = () => {
      const aspectRatio = img.width / img.height;
      const minDimension = Math.min(img.width, img.height);

      if (minDimension < 100 || aspectRatio < 0.5 || aspectRatio > 2) {
        Swal.fire({
          title: "Invalid Image Dimensions",
          text: "Please use a square or near-square logo with minimum dimensions of 100x100px. Recommended: 250x250px.",
          icon: "error",
          confirmButtonText: "Choose Another Image",
        });
        URL.revokeObjectURL(preview);
        return;
      }

      setFormData((prev) => {
        revokeBlobPreview(prev.preview);
        return { ...prev, file, preview };
      });
    };

    img.onerror = () => {
      Swal.fire("Error!", "Invalid image file.", "error");
      URL.revokeObjectURL(preview);
    };

    img.src = preview;
  };

  const removePreviewImage = () => {
    setFormData((prev) => {
      revokeBlobPreview(prev.preview);
      return { ...prev, file: null, preview: null };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;

    if (!formData.name.trim()) {
      Swal.fire(
        "Error!",
        isEditMode
          ? "Please enter a brand name."
          : "Please fill in all required fields.",
        "error",
      );
      return;
    }

    if (!NAME_PATTERN.test(formData.name.trim())) {
      Swal.fire(
        "Error!",
        "Brand name can only contain letters and spaces.",
        "error",
      );
      return;
    }

    if (!isEditMode && !formData.file) {
      Swal.fire("Error!", "Please fill in all required fields.", "error");
      return;
    }

    if (isEditMode && !formData.file && !formData.preview) {
      Swal.fire("Error!", "Please provide a brand logo.", "error");
      return;
    }

    setIsSaving(true);
    try {
      await onSubmit({
        file: formData.file,
        name: formData.name.trim(),
        brandName_ur: formData.brandName_ur.trim(),
        image_alt: formData.image_alt,
        image_title: formData.image_title,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const nameValid =
    formData.name.trim().length > 0 && NAME_PATTERN.test(formData.name.trim());
  const imageValid = isEditMode
    ? Boolean(formData.file || formData.preview)
    : Boolean(formData.file);
  const isFormValid = nameValid && imageValid;
  const nameHasError =
    formData.name.trim().length > 0 && !NAME_PATTERN.test(formData.name.trim());

  return (
    <UiModal
      isOpen={isOpen}
      onClose={handleClose}
      eyebrow="Brands"
      title={isEditMode ? "Edit Brand" : "Add Brand"}
      as="form"
      onSubmit={handleSubmit}
      footer={
        <>
          <button
            type="button"
            className="ui-btn-cancel min-w-[108px]"
            onClick={handleClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="ui-btn-save min-w-[108px]"
            disabled={isSaving || !isFormValid}
          >
            {isSaving
              ? isEditMode
                ? "Updating..."
                : "Saving..."
              : isEditMode
                ? "Update"
                : "Save"}
          </button>
        </>
      }
    >
      <section className="ui-modal-section">
        <h4 className="ui-modal-section-title">Media</h4>
        <div className="space-y-3">
          <label className="ui-form-label">
            Brand Logo{" "}
            {!isEditMode ? <span className="text-red-500">*</span> : null}
          </label>

          <input
            id="brand-logo-upload"
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />

          {formData.preview ? (
            <div className="relative">
              <div className="h-48 w-full overflow-hidden rounded-lg border-2 border-gray-200 bg-gray-100">
                <ImageWithFallback
                  src={formData.preview}
                  alt="Brand logo preview"
                  className="h-full w-full object-contain"
                />
              </div>
              <button
                type="button"
                onClick={removePreviewImage}
                className="absolute right-2 top-2 rounded-full bg-red-500 p-2 text-white transition-colors hover:bg-red-600"
                title="Remove image"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
              <label
                htmlFor="brand-logo-upload"
                className="mt-2 inline-flex cursor-pointer text-sm font-medium text-[#982598] hover:underline"
              >
                Change image
              </label>
            </div>
          ) : (
            <label
              htmlFor="brand-logo-upload"
              className="flex h-48 w-full cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 transition-colors hover:border-[#982598]/50 hover:bg-[#982598]/5"
            >
              <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm">
                <CameraIcon className="h-6 w-6 text-[#982598]" />
              </span>
              <span className="font-medium text-gray-700">Upload Logo</span>
              <span className="mt-1 text-sm text-gray-400">
                Click to browse files
              </span>
            </label>
          )}

          <p className="text-xs text-gray-500">
            Required: Square logo (min 100x100px, recommended 250x250px). Max
            file size: 2MB
          </p>
          <ImageSeoFields
            imageAlt={formData.image_alt}
            imageTitle={formData.image_title}
            onAltChange={(value) =>
              setFormData((prev) => ({ ...prev, image_alt: value }))
            }
            onTitleChange={(value) =>
              setFormData((prev) => ({ ...prev, image_title: value }))
            }
          />
        </div>
      </section>

      <section className="ui-modal-section">
        <h4 className="ui-modal-section-title">Brand details</h4>
        <div>
          <label htmlFor="brand-name" className="ui-form-label">
            Brand Name <span className="text-red-500">*</span>
          </label>
          <input
            id="brand-name"
            type="text"
            value={formData.name}
            onChange={(e) => {
              const value = e.target.value;
              if (NAME_INPUT_PATTERN.test(value)) {
                setFormData((prev) => ({ ...prev, name: value }));
              }
            }}
            placeholder="Enter brand name (alphabets only)"
            maxLength={50}
            className={`ui-input-field ${
              nameHasError ? "border-brand-error" : ""
            }`}
          />
          {nameHasError ? (
            <p className="ui-form-error">
              Brand name can only contain letters and spaces
            </p>
          ) : null}
          <UrduInput
            label="برانڈ کا نام"
            name="brandName_ur"
            value={formData.brandName_ur}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                brandName_ur: e.target.value,
              }))
            }
          />
          <p className="mt-1 text-xs text-gray-500">
            Only alphabets and spaces allowed
          </p>
        </div>
      </section>
    </UiModal>
  );
}

export default BrandModal;
